import { Alert, Box, Card, DuoColorIcon, Icon, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconBankFilled, IconDeleteFilled, IconSyncFilled } from '@zillow/constellation-icons';

import type { RenterConfigInterface, RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import AvailablePaymentMethods from '@renter-hub/components/AvailablePaymentMethods';
import PlaidButton from '@common/components/PlaidButton';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

interface Props {
  formattedAddress: string;
  onPaymentMethodClick: (paymentMethod: RenterPaymentMethodInterface) => void;
  revokedPaymentMethods: Array<RenterPaymentMethodInterface>;
  hasOtherPaymentMethods: boolean;
  config: RenterConfigInterface;
  getPlaidUpdateModeLinkToken: (externalId?: string) => any;
  onReauthSuccess: (data: any, paymentMethod: RenterPaymentMethodInterface) => void;
  onPlaidExit: (err: any, metadata: any) => void;
  onDeletePaymentMethod: (paymentMethod: RenterPaymentMethodInterface) => void;
  userZuid?: string;
}

export const DisplayPaymentMethods = ({
  formattedAddress,
  onPaymentMethodClick,
  revokedPaymentMethods,
  hasOtherPaymentMethods,
  config,
  getPlaidUpdateModeLinkToken,
  onReauthSuccess,
  onPlaidExit,
  onDeletePaymentMethod,
  userZuid,
}: Props) => {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Paragraph>{`You can use any of the linked payment methods below to make payments for ${formattedAddress}.`}</Paragraph>
      {revokedPaymentMethods.length > 0 && (
        <Box
          role="region"
          aria-label="Payment methods needing attention"
          css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
        >
          <Text textStyle="body-bold">Needs your attention</Text>
          <Alert tone="warning">
            We lost the verified connection to your bank account. To avoid delays with your payment, reconnect with
            Plaid.
          </Alert>
          {revokedPaymentMethods.map((paymentMethod) => (
            <Card key={paymentMethod.id} tone="soft" elevated={false}>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'default' }}>
                  <Box css={{ display: 'flex', alignItems: 'center', gap: 'default', flex: '1' }}>
                    <DuoColorIcon tone="warning">
                      <Icon>
                        <IconBankFilled />
                      </Icon>
                    </DuoColorIcon>
                    <Box css={{ display: 'flex', flexDirection: 'column' }}>
                      <Text textStyle="body-bold">{paymentMethod.nickname || paymentMethod.displayName}</Text>
                      <Text>(...{paymentMethod.accountLast4})</Text>
                    </Box>
                  </Box>
                  <IconButton
                    tone="neutral"
                    emphasis="bare"
                    shape="circle"
                    size="sm"
                    title="Delete payment method"
                    onClick={() => onDeletePaymentMethod(paymentMethod)}
                    icon={<IconDeleteFilled />}
                  />
                </Box>
                <Text>Connection expired</Text>
                <PlaidButton
                  plaidEnv={config.plaidEnv}
                  plaidLinkToken={config.plaidLinkToken}
                  plaidLinkTokenExpiration={config.plaidLinkTokenExpiration}
                  getPlaidLinkToken={() => getPlaidUpdateModeLinkToken(paymentMethod.id)}
                  label="Reconnect account"
                  icon={<IconSyncFilled />}
                  full={true}
                  size="sm"
                  emphasis="outlined"
                  onSuccess={(data) => onReauthSuccess(data, paymentMethod)}
                  onExit={onPlaidExit}
                  useConstellationButton={true}
                  userZuid={userZuid}
                />
              </Box>
            </Card>
          ))}
        </Box>
      )}
      {hasOtherPaymentMethods && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          {revokedPaymentMethods.length > 0 && <Text textStyle="body-bold">Other payment methods</Text>}
          <AvailablePaymentMethods
            onPaymentMethodClick={onPaymentMethodClick}
            filterStatuses={[VERIFICATION_STATUS.PLAID_REVOKED]}
          />
        </Box>
      )}
    </Box>
  );
};
